import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Shield, Loader2 } from "lucide-react";
import { toast } from "sonner";
import AppLayout from "../components/AppLayout";
import { api } from "../lib/api";
import { StatusBadge, EnvBadge, errMsg } from "../components/takedown/TakedownBits";

const lines = (s) => s.split(/[\n,]+/).map((x) => x.trim()).filter(Boolean);
const EMPTY = { stage_name: "", real_user_name: "", query: "", user_domains: "", user_accounts: "", confirm_production: false };
const inputCls = "w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white";

export default function ContentProtection() {
  const [config, setConfig] = useState(null);
  const [projects, setProjects] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setLoadError("");
      const [c, p] = await Promise.all([api.get("/api/takedowns/config"), api.get("/api/takedowns/projects")]);
      setConfig(c.data);
      setProjects(p.data);
    } catch (e) {
      setLoadError(errMsg(e, "Failed to load campaigns"));
      setProjects([]);
    }
  };
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.stage_name.trim()) return toast.error("Stage name is required");
    if (lines(form.query).length === 0) return toast.error("At least one search query is required");
    const badUrl = [...lines(form.user_domains), ...lines(form.user_accounts)].find((u) => !u.startsWith("https://"));
    if (badUrl) return toast.error(`Only HTTPS URLs are allowed: ${badUrl}`);
    if (config?.requires_production_confirmation && !form.confirm_production) return toast.error("Confirm production before creating a campaign");
    setSaving(true);
    try {
      await api.post("/api/takedowns/projects/model", {
        stage_name: form.stage_name,
        real_user_name: form.real_user_name,
        query: lines(form.query),
        user_domains: lines(form.user_domains),
        user_accounts: lines(form.user_accounts),
        confirm_production: form.confirm_production,
      });
      toast.success("Protection campaign created");
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(errMsg(err, "Could not create campaign"));
      load();
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppLayout
      title="Content Protection"
      overline="TAKEDOWN.CO · CREATOR PROTECTION"
      actions={config && <EnvBadge environment={config.environment} />}
    >
      <div className="p-4 sm:p-6 md:p-10 space-y-8 max-w-5xl">
        {config && !config.configured && (
          <div className="rounded-xl border border-yellow-500/30 bg-yellow-500/10 text-yellow-200 text-sm p-4" data-testid="takedown-not-configured">
            Takedown API is not fully configured on the server (base URL, API key, auth header). Campaign creation will fail until it is.
          </div>
        )}
        {config && config.environment === "invalid" && (
          <div className="rounded-xl border border-red-500/30 bg-red-500/10 text-red-200 text-sm p-4" data-testid="takedown-invalid-env">
            TAKEDOWN_ENVIRONMENT has an unknown value. Requests are blocked until it is set to sandbox or production.
          </div>
        )}
        {config && !config.send_enabled && (
          <div className="rounded-xl border border-blue-500/30 bg-blue-500/10 text-blue-200 text-sm p-4" data-testid="takedown-send-disabled">
            Outbound Takedown requests are disabled on the server. Campaigns cannot be created and notices stay queued locally.
          </div>
        )}

        <form onSubmit={submit} className="rounded-2xl border border-white/10 bg-white/5 p-5 space-y-4" data-testid="campaign-form">
          <h2 className="font-semibold flex items-center gap-2"><Shield size={18} className="text-purple-400" /> New protection campaign</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="text-sm space-y-1">Stage name *
              <input className={inputCls} value={form.stage_name} onChange={set("stage_name")} data-testid="stage-name-input" />
            </label>
            <label className="text-sm space-y-1">Real / display name
              <input className={inputCls} value={form.real_user_name} onChange={set("real_user_name")} />
            </label>
          </div>
          <label className="text-sm space-y-1 block">Search queries * (one per line)
            <textarea rows={3} className={inputCls} value={form.query} onChange={set("query")} data-testid="query-input" />
          </label>
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="text-sm space-y-1">Official websites (HTTPS, one per line)
              <textarea rows={3} className={inputCls} value={form.user_domains} onChange={set("user_domains")} />
            </label>
            <label className="text-sm space-y-1">Official accounts (HTTPS, one per line)
              <textarea rows={3} className={inputCls} value={form.user_accounts} onChange={set("user_accounts")} />
            </label>
          </div>
          {config?.requires_production_confirmation && (
            <label className="text-sm flex items-center gap-2 text-red-200" data-testid="confirm-production">
              <input type="checkbox" checked={form.confirm_production} onChange={(e) => setForm({ ...form, confirm_production: e.target.checked })} />
              I confirm this creates a live PRODUCTION campaign
            </label>
          )}
          <button disabled={saving} className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-500 text-sm font-semibold disabled:opacity-50 flex items-center gap-2" data-testid="create-campaign-btn">
            {saving && <Loader2 size={14} className="animate-spin" />} Create campaign
          </button>
        </form>

        <section>
          <h2 className="font-semibold mb-3">Campaigns</h2>
          {loadError && <div className="text-red-300 text-sm mb-3" data-testid="campaigns-error">{loadError}</div>}
          {projects === null ? (
            <div className="text-gray-400 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>
          ) : projects.length === 0 ? (
            <div className="text-gray-500 text-sm border border-dashed border-white/10 rounded-xl p-8 text-center" data-testid="campaigns-empty">
              No campaigns yet. Create one above to start protecting a creator.
            </div>
          ) : (
            <div className="space-y-2">
              {projects.map((p) => (
                <Link key={p.id} to={`/crm/content-protection/${p.id}`} className="flex items-center justify-between rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 px-4 py-3" data-testid="campaign-row">
                  <div>
                    <div className="font-medium">{p.stage_name}</div>
                    <div className="text-xs text-gray-500">{new Date(p.created_at).toLocaleString()} · {p.environment}</div>
                  </div>
                  <StatusBadge status={p.status} />
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>
    </AppLayout>
  );
}
